import config from '@/config'
import mixpanelUtils from '@/utils/mixpanel.utils'

export const COMPONENT_USAGE_EVENT = 'Component Usage Sample'

const SAMPLED_KEY = 'componentUsageSampled'
const SENT_KEY = 'componentUsageSent'
const FLUSH_DELAY_MS = 15000
// Keeps each list property inside Mixpanel's list-size and event-size limits.
const MAX_ITEMS_PER_EVENT = 100
const MAX_LIST_BYTES_PER_EVENT = 7000
const SRC_PREFIX = 'src/'
const NO_ROUTE = '(none)'

const parseRate = (value) => {
  const rate = Number(value)
  if (!Number.isFinite(rate) || rate <= 0) return 0
  return Math.min(rate, 1)
}

const isSessionSampled = (rate) => {
  try {
    const stored = sessionStorage.getItem(SAMPLED_KEY)
    if (stored === '1' || stored === '0') return stored === '1'
    const sampled = Math.random() < rate
    sessionStorage.setItem(SAMPLED_KEY, sampled ? '1' : '0')
    return sampled
  } catch {
    return false
  }
}

const readSent = () => {
  try {
    const sent = JSON.parse(sessionStorage.getItem(SENT_KEY) || '[]')
    return Array.isArray(sent) ? sent : []
  } catch {
    return []
  }
}

const writeSent = (seen) => {
  try {
    sessionStorage.setItem(SENT_KEY, JSON.stringify([...seen]))
  } catch {
    // Storage full or blocked: the next page load re-reports, which only costs volume.
  }
}

const chunkList = (items) => {
  const chunks = []
  let current = []
  let bytes = 2
  items.forEach((item) => {
    const itemBytes = item.length + 3
    if (current.length && (current.length >= MAX_ITEMS_PER_EVENT || bytes + itemBytes > MAX_LIST_BYTES_PER_EVENT)) {
      chunks.push(current)
      current = []
      bytes = 2
    }
    current.push(item)
    bytes += itemBytes
  })
  if (current.length) chunks.push(current)
  return chunks
}

const appPlatform = () => {
  if (config.integrationPlatform) return config.integrationPlatform
  return config.isEnableAddinPowerpoint ? 'PowerPointAddin' : 'web'
}

const eventProps = (components, routeName, rate) => ({
  components,
  component_count: components.length,
  route_name: routeName,
  app_env: config.env,
  app_region: config.region,
  app_platform: appPlatform(),
  app_version: config.sentryRelease || '',
  sample_rate: rate,
})

const createCollector = (rate) => {
  const seen = new Set(readSent())
  const pendingByRoute = new Map()
  let flushTimer = null

  const flush = () => {
    clearTimeout(flushTimer)
    flushTimer = null
    if (!pendingByRoute.size) return
    const batches = [...pendingByRoute]
    pendingByRoute.clear()
    writeSent(seen)
    batches.forEach(([routeName, files]) => {
      chunkList([...files]).forEach((components) => {
        mixpanelUtils.track(COMPONENT_USAGE_EVENT, eventProps(components, routeName, rate))
      })
    })
  }

  const record = (file, routeName) => {
    seen.add(file)
    const key = routeName || NO_ROUTE
    if (!pendingByRoute.has(key)) pendingByRoute.set(key, new Set())
    pendingByRoute.get(key).add(file.slice(SRC_PREFIX.length))
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_DELAY_MS)
  }

  return { seen, record, flush }
}

export const installComponentUsage = (Vue, rawRate) => {
  const rate = parseRate(rawRate)
  if (!rate || !isSessionSampled(rate)) return false

  const { seen, record, flush } = createCollector(rate)
  Vue.mixin({
    mounted() {
      const file = this.$options.__file
      if (file && file.startsWith(SRC_PREFIX) && !seen.has(file)) record(file, this.$route && this.$route.name)
    },
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('pagehide', flush)
  return true
}
